import React from 'react';
import {getBucketByAccuracy} from 'lib/StatsUtil';
import {GuideModelV1} from 'resources/augmented/Guide/GuideModel.v1';

import './accuracy-badge.scss';
import {Text, WithTooltip} from '@albert-io/atomic';

interface Props {
  guideLevel: GuideModelV1;
  metadata: React.ReactNode;
  partialCreditEnabled: boolean;
}

const MIN_REQUIRED_QUESTIONS = 5;
const REQUIRED_PERCENTAGE = 0.1; // 10%

function AccuracyBadge({guideLevel, metadata, partialCreditEnabled}: Props) {
  const topic = guideLevel;

  // If a user is logged in they see one of three possible statuses:
  // 1. a 'Learning' label showing them that they are required to answer more questions before they can see their accuracy
  // 2. a label showing them their raw accuracy
  // 3. the default "Not started" label if they have answered nothing.

  if (topic.getMetaCountOfUniqueQuestionsAnswered() === 0) {
    return (
      <div className='u-align_items_center u-pad-r_1 flex text-nowrap'>
        {!guideLevel.isTheme() ? (
          <Text size='m' className='u-pad_1'>
            {metadata}
          </Text>
        ) : null}
        <Text size='m' className='u-pad_1'>
          Not Started
        </Text>
      </div>
    );
  }

  // If a user has not answered 10% of the guide's questions, they will not be shown their accuracy,
  // but will instead see a Learning badge with a tooltip letting them know
  // how many remaining questions they have to answer.

  // 10% or 5 -- whatever is smaller. Rounding the percentage value so we get less odd results.
  // ex 10% of 17 is 1.7, but we can round that to be 2 so the user needs to answer 2 questions and not 1
  const requiredAnswered = Math.min(
    Math.round(topic.getMetaCountOfQuestions() * REQUIRED_PERCENTAGE),
    MIN_REQUIRED_QUESTIONS
  );
  const isLearning = topic.getMetaCountOfUniqueQuestionsAnswered() < requiredAnswered;
  const topicAccuracy =
    (topic.getIn([
      'meta',
      partialCreditEnabled ? 'student_partial_credit_accuracy' : 'student_accuracy'
    ]) ?? 0) / 100;

  const formattedTopicAccuracy = topicAccuracy.toLocaleString(undefined, {style: 'percent'});

  const color = getBucketByAccuracy(topicAccuracy).COLOR;

  return (
    <div className='u-align_items_center u-pad-r_1 flex text-nowrap'>
      {!guideLevel.isTheme() ? (
        <Text size='m' className='u-pad_1'>
          {metadata}
        </Text>
      ) : null}
      {isLearning ? (
        <WithTooltip
          content={`You need to answer at least ${requiredAnswered} questions before you can see your accuracy.`}
          placement='bottom'
          display='flex'
        >
          <Text
            size='m'
            className='accuracy-badge__accuracy u-pad_1 u-border-l u-border-color_primary'
            style={{
              color: `var(--${color}-bold)`
            }}
          >
            Learning
          </Text>
        </WithTooltip>
      ) : (
        <Text
          size='m'
          className='accuracy-badge__accuracy u-pad_1 u-border-l u-border-color_primary'
          style={{
            color: `var(--${color}-bold)`
          }}
        >{`${formattedTopicAccuracy} Accuracy`}</Text>
      )}
    </div>
  );
}

export default AccuracyBadge;
